import React, { useState } from 'react';
import moment from 'moment';
import 'moment/locale/es'; // Import Spanish locale
import { useIntl } from 'react-intl';
import styles from './GoogleStyleReviews.module.css';
import { PrimaryButton } from '../../../components';

// Set Spanish locale
moment.locale('es');

const GoogleStyleReviews = () => {
  const intl = useIntl();

  // State to track which text is showing for each review
  const [showOriginal, setShowOriginal] = useState({});

  // Function to toggle between original and current text
  const toggleText = reviewId => {
    setShowOriginal(prevState => ({
      ...prevState,
      [reviewId]: !prevState[reviewId],
    }));
  };

  // JSON array with review data extracted from the HTML
  const reviews = [
    {
      id: 1,
      name: 'Guillermo Salazar',
      rating: `5 ${intl.formatMessage({ id: 'googleReviews.reviews' })}`,
      badge: intl.formatMessage({ id: 'googleReviews.newBadge' }),
      original:
        'Método seguro y de confianza para préstamo de material entre particulares del mismo sector. He hecho uso del servicio un par de veces, y siempre he tenido buenas experiencias.',
      text: intl.formatMessage({ id: 'googleReviews.review1.text' }),
      stars: 5,
      date: moment().subtract(3, 'weeks'), // 3 weeks ago
      initial: 'G',
    },
    {
      id: 2,
      name: 'Maria Laura Ferreira Niño',
      rating: `6 ${intl.formatMessage({ id: 'googleReviews.reviews' })} · 2 ${intl.formatMessage({
        id: 'googleReviews.photos',
      })}`,
      badge: intl.formatMessage({ id: 'googleReviews.newBadge' }),
      original: 'Un excelente servicio con precios asequibles para llevar a cabo tus proyectos!!',
      text: intl.formatMessage({ id: 'googleReviews.review2.text' }),
      stars: 5,
      date: moment()
        .subtract(3, 'weeks')
        .subtract(2, 'days'), // 3 weeks and 2 days ago
      initial: 'M',
    },
    {
      id: 3,
      name: 'Irati Santiago Mujika',
      rating: `${intl.formatMessage({ id: 'googleReviews.localGuide' })} · 19 ${intl.formatMessage({
        id: 'googleReviews.reviews',
      })}`,
      badge: '',
      original:
        'Excelente servicio de alquiler de equipo. Es una empresa nueva, pero desde el primer momento se nota la seriedad y el compromiso con el cliente.',
      text: intl.formatMessage({ id: 'googleReviews.review3.text' }),
      stars: 5,
      date: moment().subtract(1, 'month'), // 1 month ago
      initial: 'I',
    },
    {
      id: 4,
      name: 'Aitor Echevarria',
      rating: `1 ${intl.formatMessage({ id: 'googleReviews.reviews' })}`,
      badge: intl.formatMessage({ id: 'googleReviews.newBadge' }),
      original:
        'Necesitábamos una cámara y un micro para una grabación urgente y nos sacaron del apuro. Servicio ágil y material en perfecto estado.',
      text: intl.formatMessage({ id: 'googleReviews.review4.text' }),
      stars: 5,
      date: moment()
        .subtract(3, 'weeks')
        .subtract(1, 'day'), // 3 weeks and 1 day ago
      initial: 'A',
    },
    {
      id: 5,
      name: 'Carlos Rodríguez',
      rating: `8 ${intl.formatMessage({ id: 'googleReviews.reviews' })}`,
      badge: intl.formatMessage({ id: 'googleReviews.newBadge' }),
      original:
        'Muy buena experiencia con el servicio. Rápido, eficiente y con equipos de calidad. Lo recomiendo totalmente.',
      text: intl.formatMessage({ id: 'googleReviews.review5.text' }),
      stars: 5,
      date: moment().subtract(2, 'weeks'), // 2 weeks ago
      initial: 'C',
    },
    {
      id: 6,
      name: 'Ana Martínez',
      rating: `${intl.formatMessage({ id: 'googleReviews.localGuide' })} · 32 ${intl.formatMessage({
        id: 'googleReviews.reviews',
      })}`,
      badge: '',
      original:
        'Fantástico servicio al cliente y equipos en perfecto estado. Volveré a usar sus servicios sin duda.',
      text: intl.formatMessage({ id: 'googleReviews.review6.text' }),
      stars: 5,
      date: moment()
        .subtract(1, 'month')
        .subtract(1, 'week'), // 1 month and 1 week ago
      initial: 'A',
    },
  ];

  // Function to render star ratings
  const renderStars = count => {
    return (
      <div className={styles.stars}>
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className={styles.star}>
            &#9733;
          </span>
        ))}
      </div>
    );
  };

  // Function to generate a color based on the initial
  const getAvatarColor = initial => {
    const colors = ['#1e88e5', '#43a047', '#fbc02d', '#e53935', '#8e24aa', '#fb8c00'];
    const index = initial.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{intl.formatMessage({ id: 'googleReviews.title' })}</h1>

      <div className={styles.reviewsContainer}>
        <div className={styles.reviewsRow}>
          {reviews.map(review => (
            <div key={review.id} className={styles.reviewCard}>
              <div className={styles.reviewHeader}>
                <div
                  className={styles.avatar}
                  style={{ backgroundColor: getAvatarColor(review.initial) }}
                >
                  {review.initial}
                </div>
                <div className={styles.reviewerInfo}>
                  <h3 className={styles.reviewerName}>{review.name}</h3>
                  <div className={styles.reviewMeta}>
                    {renderStars(review.stars)}
                    <span className={styles.reviewTime}>{review.date.fromNow()}</span>
                  </div>
                  <div className={styles.reviewStats}>{review.rating}</div>
                </div>
              </div>

              <div className={styles.reviewContent}>
                <p className={styles.reviewText}>
                  {showOriginal[review.id] ? review.original : review.text}
                </p>

                <button className={styles.toggleButton} onClick={() => toggleText(review.id)}>
                  {showOriginal[review.id]
                    ? intl.formatMessage({ id: 'googleReviews.viewCurrentText' })
                    : intl.formatMessage({ id: 'googleReviews.viewOriginalText' })}
                </button>

                {showOriginal[review.id] && (
                  <div className={styles.textIndicator}>
                    <span className={styles.indicatorLabel}>
                      {intl.formatMessage({ id: 'googleReviews.showing' })}
                    </span>
                    <span className={styles.indicatorValue}>
                      {intl.formatMessage({ id: 'googleReviews.originalText' })}
                    </span>
                  </div>
                )}
              </div>

              {review.badge && <div className={styles.badge}>{review.badge}</div>}
            </div>
          ))}
        </div>
      </div>

      <a
        target="_blank"
        href="https://www.google.com/maps/place/aloka/@43.30992,-1.9785149,717m/data=!3m1!1e3!4m8!3m7!1s0xd51af64b850afc3:0x6a27ad7d31073bb8!8m2!3d43.30992!4d-1.97594!9m1!1b1!16s%2Fg%2F11w1k0pxyh?entry=ttu&g_ep=EgoyMDI1MDgxOS4wIKXMDSoASAFQAw%3D%3D"
        className={styles.actions}
      >
        <PrimaryButton className={styles.leaveReviewBtn}>
          {intl.formatMessage({ id: 'googleReviews.leaveReview' })}
        </PrimaryButton>
      </a>
    </div>
  );
};

export default GoogleStyleReviews;
